import * as Rest from 'src/constants/rest'
import { EstadoEscrito, TipoEscrito, TipoMensaje } from 'src/enums/EscritosLegales'
import { Escrito } from 'src/interfaces/EscritosLegales'
import http from 'src/utils/Http'
import HttpError from 'src/utils/Http/HttpError'

export interface GetEscritoResponse {
  idEnvio: number
  idEscrito: number
  tamBloque: number
  token: string
  bytesMIME: string
}

interface GetMensajeEscritoCadesCorreccionResponse {
  base64: string
  fileName: string
  fileSize: number
}
interface logErrorType {
  origen: string
  error: any
  traza: string | undefined
  trazaDev: string
}

interface GetMensajeEscritoCadesResponse {
  mensajeBase64: string
  correccionPdf: GetMensajeEscritoCadesCorreccionResponse
}

interface GetEscritoJsonResponse {
  escrito: Escrito
}

export interface EscritoAdjuntosPost {
  base64: string
  fileName: string
  fileSize: number
  firma: string
}

export function sendEscrito(escrito: Escrito): Promise<GetEscritoResponse> {
  return http
    .post({
      url: Rest.LEGAL_WRITING_SEND,
      token: true,
      params: {
        escrito
      }
    })
    .then(res => ({
      idEnvio: res.idEnvio,
      idEscrito: res.idEscrito,
      tamBloque: res.tamBloque,
      token: res.token,
      bytesMIME: res.bytesMiME || res.bytesMIME
    }))
    .catch((error: Error) => {
      throw error
    })
}

export function getMensajeEscritoCades(
  idEnvio: number,
  signedToken: string,
  files: (EscritoAdjuntosPost | null)[],
  isFixed: boolean
): Promise<GetMensajeEscritoCadesResponse> {
  const headers = {
    'Content-type': 'multipart/form-data'
  }

  return http
    .post({
      url: Rest.LEGAL_WRITING_GET_CADES_MESSAGE,
      token: true,
      headers,
      params: {
        idEnvio,
        firmaToken: signedToken,
        firmaTokenHeader: signedToken,
        adjuntos: JSON.stringify(files),
        esCorreccion: isFixed
      }
    })
    .then(res => ({
      mensajeBase64: res.mensajeBase64,
      correccionPdf: res.correccionPdf
    }))
}

export function sendEscritoBloque(
  idEnvio: number,
  idEscrito: number,
  signedToken: string,
  signedCadesMessage: string,
  files: (EscritoAdjuntosPost | null)[],
  tamBloque: number
): Promise<GetEscritoJsonResponse> {
  const headers = {
    'Content-type': 'multipart/form-data'
  }
  return http
    .post({
      url: Rest.LEGAL_WRITING_SEND_BLOCK,
      token: true,
      headers,
      params: {
        idEnvio,
        idEscrito,
        firmaToken: signedToken,
        firmaMensajeCades: signedCadesMessage,
        adjuntos: JSON.stringify(files),
        tamBloque
      }
    })
    .then(res => ({
      escrito: res
    }))
    .catch((error: HttpError) => {
      throw error
    })
}

export function getEscrito(idAcuse: number): Promise<GetEscritoJsonResponse> {
  return http
    .post({
      url: Rest.LEGAL_WRITING_GET,
      token: true,
      params: {
        filterCriteria: { idAcuse }
      }
    })
    .then(res => ({
      escrito: res
    }))
}

export function logError({
  origen,
  error,
  traza = '',
  trazaDev = ''
}: logErrorType): Promise<string> {
  const errorText =
    Array.isArray(error) || typeof error === 'object' ? JSON.stringify(error) : error.toString()
  return http.post({
    url: Rest.ERROR_LOG,
    token: true,
    params: {
      origen,
      error: errorText,
      traza,
      trazaDev
    }
  })
}

export interface GetEscritosResponse {
  results: Escrito[]
  total: number
  page: number
  itemsPerPage: number
}

export function getEscritos(
  filters: { estado: EstadoEscrito[]; tipoEscrito: TipoMensaje[]; tipo: TipoEscrito[] },
  page = 1,
  itemsPerPage = 10,
  sortBy: { id: string; desc: boolean }[],
  searchedText: string
): Promise<GetEscritosResponse> {
  const sortByQuery = sortBy.reduce(
    (acc, item) => ({
      ...acc,
      [`order[${item.id}]`]: item.desc ? 'desc' : 'asc'
    }),
    {}
  )
  return http
    .get({
      url: Rest.ESCRITOS,
      token: true,
      params: {
        itemsPerPage,
        page,
        'estado[]': filters.estado,
        'tipo[]': filters.tipo,
        'tipoEscrito[]': filters.tipoEscrito,
        searchedText,
        ...sortByQuery
      }
    })
    .then(res => ({
      results: res.escritos,
      total: res.total,
      page,
      itemsPerPage: res.itemsPerPage
    }))
}

export interface SaveEscritosResponse {
  results: Escrito
}

export function saveEscrito(
  idEscrito: number,
  idTipo: number,
  idTipoEscrito: number,
  name: string,
  escrito: string
): Promise<SaveEscritosResponse> {
  return http
    .post({
      url: Rest.ESCRITO_SAVE,
      token: true,
      params: {
        idEscrito,
        idTipo,
        idTipoEscrito,
        name,
        escrito
      }
    })
    .then(res => ({
      results: res.escrito
    }))
}
